/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { FormulaContextLoader } from '@gh/builder-shared';
import { BulkUpdateRestService, ItemRestService, UomRestService } from '@gh/builder-shared-data';
import { getRouteParam, InitViewService, isRouteForCreationMode } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { map$, switchMap$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';
import {
  ItemDetails,
  itemDetailsFromDto,
  itemDetailsToDto,
  newItemDetails,
} from './component-and-item-details.model';

@Injectable()
export class ItemDetailsBusinessService implements InitViewService {
  constructor(private itemRest: ItemRestService,
              private uomRest: UomRestService,
              private bulkUpdateRest: BulkUpdateRestService,
              private formulaContextLoader: FormulaContextLoader) {

  }

  initView(route: ActivatedRouteSnapshot): any {
    const itemId = getRouteParam(route, 'itemId');

    return {
      uoms: this.uomRest.findAllShared(),
      formulaContext: this.formulaContextLoader.loadForEstimating(),
      item: isRouteForCreationMode(route) ? newItemDetails() : this.itemRest.get(itemId).pipe(map$(itemDetailsFromDto)),
    };
  }

  get(id: ID): Observable<ItemDetails> {
    return this.itemRest.get(id).pipe(map$(itemDetailsFromDto));
  }

  save(itemDetails: ItemDetails): Observable<ItemDetails> {
    if (itemDetails.id) {
      return this.itemRest.update(itemDetailsToDto(itemDetails)).pipe(map$(itemDetailsFromDto));
    } else {
      return this.itemRest.create(itemDetailsToDto(itemDetails)).pipe(map$(itemDetailsFromDto));
    }
  }

  saveAndPropagateFormula(item: ItemDetails): Observable<ItemDetails> {
    return this.save(item).pipe(switchMap$(
      (updatedItem) => this.bulkUpdateRest.propagateFormula({
        oldFormula: item.oldFormula,
        newFormula: updatedItem.formula,
        itemId: updatedItem.id,
      }).pipe(map$(() => updatedItem))));
  }

  getMatchingItemCodes(searchString: string): Observable<string[]> {
    return this.itemRest.getItemCodes(searchString);
  }
}
